import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { formatTime, heightByTwelfths } from "@nav-games/tides";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface TwelfthsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "tides";
const GAME = "twelfths";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  hwHeight: number;
  lwHeight: number;
  hwMinutes: number;
  hoursAfter: number;
  /// Correct height at the offset time, in metres.
  answer: number;
  choices: readonly number[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function shuffled<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = a;
  }
  return arr;
}

function buildCard(): Card {
  // Pick a plausible tide.
  const hwHeight = randInt(40, 65) / 10; // 4.0 to 6.5 m
  const lwRange = randInt(8, 28) / 10; // range 0.8 to 2.8 m? No — range needs to be larger
  const range = randInt(20, 50) / 10; // 2.0 to 5.0 m
  const lwHeight = Math.max(0.4, hwHeight - range);
  const hwMinutes = randInt(0, 23) * 60; // top of an hour
  const hoursAfter = randInt(1, 5); // ask for the height some whole hour later
  const answer = heightByTwelfths(hwHeight, lwHeight, hoursAfter, 6);
  // Round to 1dp for the question.
  const answerRounded = Math.round(answer * 10) / 10;

  // Distractor strategies:
  //  - wrong-direction (treat as flooding instead of ebbing)
  //  - swapped fall fraction (e.g. apply cum_3 instead of cum_6)
  //  - just HW or just LW
  //  - height with a wrong range
  const candidates = new Set<number>();
  candidates.add(answerRounded);
  candidates.add(Math.round(hwHeight * 10) / 10);
  candidates.add(Math.round(lwHeight * 10) / 10);
  candidates.add(
    Math.round(heightByTwelfths(lwHeight, hwHeight, hoursAfter, 6) * 10) / 10,
  );
  candidates.add(
    Math.round(heightByTwelfths(hwHeight, lwHeight, 6 - hoursAfter, 6) * 10) /
      10,
  );
  // ± 0.3 m perturbation
  candidates.add(Math.round((answerRounded + 0.3) * 10) / 10);
  candidates.add(Math.round((answerRounded - 0.3) * 10) / 10);

  const arr = Array.from(candidates).filter(
    (n) => n >= 0 && n <= hwHeight + 0.5,
  );
  const distractors = shuffled(arr.filter((n) => n !== answerRounded)).slice(
    0,
    CHOICES_PER_CARD - 1,
  );
  const choices = shuffled([answerRounded, ...distractors]);
  return {
    hwHeight,
    lwHeight,
    hwMinutes,
    hoursAfter,
    answer: answerRounded,
    choices,
  };
}

export function Twelfths({ onNavigate }: TwelfthsProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<number | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: number) => {
      if (chosen !== null || result) return;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: "twelfths",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/tides")}
      />
    );
  }

  const revealed = chosen !== null;
  const range = card.hwHeight - card.lwHeight;
  const sightTime = formatTime(card.hwMinutes + card.hoursAfter * 60);
  const cumFallen = [0, 1, 3, 6, 9, 11, 12];
  const fallenTwelfths = cumFallen[card.hoursAfter]!;
  const fall = (fallenTwelfths / 12) * range;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "tides" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · Rule of twelfths
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Tide height by twelfths</h3>
        <p style={{ marginBottom: 12 }}>
          HW at <strong>{formatTime(card.hwMinutes)}</strong>, height{" "}
          <strong>{card.hwHeight.toFixed(1)} m</strong>. LW height{" "}
          <strong>{card.lwHeight.toFixed(1)} m</strong>, six hours later.
          What is the tide height at{" "}
          <strong>{sightTime}</strong> (HW + {card.hoursAfter}h)?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.answer;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 18,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.toFixed(1)} m
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answer
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answer
                ? `✓ Correct — ${card.answer.toFixed(1)} m.`
                : `✗ Not quite — ${card.answer.toFixed(1)} m.`}
            </p>
            <div
              className="small"
              style={{
                marginTop: 8,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                whiteSpace: "pre-wrap",
              }}
            >
{`Range          = ${card.hwHeight.toFixed(1)} − ${card.lwHeight.toFixed(1)} = ${range.toFixed(1)} m
Cum fallen at hour ${card.hoursAfter} = ${fallenTwelfths}/12
Fall           = ${fallenTwelfths}/12 × ${range.toFixed(1)} = ${fall.toFixed(2)} m
Height         = HW − fall = ${card.hwHeight.toFixed(1)} − ${fall.toFixed(2)} = ${card.answer.toFixed(1)} m`}
            </div>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/tides")}>
        ← Back to Tides
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Tides
        </button>
      </div>
    </>
  );
}
